/**
 * Scroll Animations
 * 
 * Reusable scroll-triggered animations using Intersection Observer API.
 * Automatically animates elements when they come into viewport.
 * 
 * Usage:
 * - Add animation classes to HTML elements (e.g., 'animate-from-bottom')
 * - Include this script in your page
 * - Elements will automatically animate when scrolled into view
 * 
 * @author Oscar Sixtos
 * @date Last Modified: 2025-09-18
 */

class ScrollAnimations {
    constructor(options = {}) {
        // Detect mobile/tablet devices
        const isMobile = window.innerWidth <= 768;
        
        this.options = {
            threshold: options.threshold || 0.1,
            rootMargin: options.rootMargin || (isMobile ? '0px 0px -20px 0px' : '0px 0px -50px 0px'),
            animationClasses: options.animationClasses || [
                '.animate-from-bottom',
                '.animate-bottom-cta',
                '.animate-from-left',
                '.animate-from-right',
                '.animate-fade-in',
                '.animate-scale'
            ],
            ...options
        };

        this.observer = null;
        this.init();
    }

    init() {
        // Check if Intersection Observer is supported
        if (!('IntersectionObserver' in window)) {
            console.warn('Intersection Observer not supported, falling back to immediate animation');
            this.fallbackAnimation();
            return;
        }

        this.createObserver();
        this.observeElements();
        this.setupResizeHandler();
    }

    setupResizeHandler() {
        // Handle screen size changes (orientation changes, window resize)
        let resizeTimer;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                const wasMobile = this.options.rootMargin === '0px 0px -20px 0px';
                const isMobile = window.innerWidth <= 768;
                
                // Only recreate observer if mobile status changed
                if ((wasMobile && !isMobile) || (!wasMobile && isMobile)) {
                    this.options.rootMargin = isMobile ? '0px 0px -20px 0px' : '0px 0px -50px 0px';
                    
                    // Recreate observer with new settings
                    if (this.observer) {
                        this.observer.disconnect();
                    }
                    this.createObserver();
                    this.observeElements();
                }
            }, 250); // Debounce resize events
        });
    }

    createObserver() {
        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.animateElement(entry.target);
                }
            });
        }, {
            threshold: this.options.threshold,
            rootMargin: this.options.rootMargin
        });
    }

    observeElements() {
        const selector = this.options.animationClasses.join(', ');
        const elements = document.querySelectorAll(selector);
        
        elements.forEach(el => {
            this.observer.observe(el);
        });

        console.log(`ScrollAnimations: Observing ${elements.length} elements`);
    }

    animateElement(element) {
        element.classList.add('animate');
        
        // Stop observing this element after animation
        if (this.observer) {
            this.observer.unobserve(element);
        }
    }

    fallbackAnimation() {
        // For browsers that don't support Intersection Observer
        const selector = this.options.animationClasses.join(', ');
        const elements = document.querySelectorAll(selector);
        
        elements.forEach(el => {
            el.classList.add('animate');
        });
    }

    // Method to manually trigger animation for specific elements
    animateElementsNow(selector) {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
            this.animateElement(el);
        });
    }

    // Method to reset animations (useful for testing or re-triggering)
    resetAnimations() {
        const selector = this.options.animationClasses.join(', ');
        const elements = document.querySelectorAll(selector);
        
        elements.forEach(el => {
            el.classList.remove('animate');
            if (this.observer) {
                this.observer.observe(el);
            }
        });
    }

    // Cleanup method
    destroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
    // Initialize with default options
    window.scrollAnimations = new ScrollAnimations();
});

// Export for module usage if needed
if (typeof module !== 'undefined' && module.exports) {
    module.exports = ScrollAnimations;
}